import { useGuestLifetimeSpend } from 'restaurantAdmin/hooks/useGuestLifetimeSpend';
import { centsToDollar, formatMoney } from '@shared/utils/currency';
import { toShortNumericDateFormat } from '@shared/utils/dateFormatters';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import receiptStyles from '../reservationGuestSheetReceipt/ReservationGuestSheetReceipt.scss';
import styles from './GuestLifetimeSpend.scss';

export interface GuestLifetimeSpendProps {
  guestId: string;
  className?: string;
}

// Supplementary to the guest's history, so loading and error states are silent
// rather than competing with the rest of the guest book. Shown behind the
// lightspeedIntegration flag, for every restaurant.
export const GuestLifetimeSpend = ({
  guestId,
  className,
}: GuestLifetimeSpendProps) => {
  const { data: lifetimeSpend, isError } = useGuestLifetimeSpend(guestId);

  if (isError || !lifetimeSpend) {
    return null;
  }

  // Tables are Peak payments, so always USD; food and beverage comes with the POS's currency.
  const foodAndBeverage =
    lifetimeSpend.foodAndBeverage === null
      ? 'N/A'
      : formatMoney(
          lifetimeSpend.foodAndBeverage,
          lifetimeSpend.foodAndBeverageCurrency ?? 'USD',
        );
  const total =
    lifetimeSpend.total === null ? 'N/A' : centsToDollar(lifetimeSpend.total);

  return (
    <section className={cx(receiptStyles.section, className)}>
      <h3 className={typography.h7_20}>
        <span>Guest&apos;s Lifetime Spend</span>
        {lifetimeSpend.since && (
          <span className={styles.range}>
            {`${toShortNumericDateFormat(lifetimeSpend.since)} - Present`}
          </span>
        )}
      </h3>
      <dl className={typography.h7}>
        <div>
          <dt>Table(s)</dt>
          <dd>{centsToDollar(lifetimeSpend.tables)}</dd>
        </div>
        <div>
          <dt>Food and Beverage</dt>
          <dd>{foodAndBeverage}</dd>
        </div>
        <div className={typography.h7_20}>
          <dt>
            <strong>Total</strong>
          </dt>
          <dd>
            <strong>{total}</strong>
          </dd>
        </div>
      </dl>
    </section>
  );
};
